import {
  useEffect,
  useState,
  type ClipboardEvent,
  type CSSProperties,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import { Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import { InputBarModelPill } from "./input-bar-model-pill";
import { ImagePreviewStrip } from "./image-preview-strip";
import { ComposerFilePicker } from "./composer-file-picker";
import { InputBarMicButton } from "./input-bar-actions";
import type { DictationMicControl, InputBarProps } from "./input-bar-types";
import type { StandaloneBarChip } from "./standalone-bar-draft";
import {
  StandaloneBarField,
  type ComposerFieldHandle,
} from "./standalone-bar-field";
import type { StandaloneBarPlaceholder } from "./standalone-bar-placeholder";
import { REVEAL_MS, type RevealStage } from "@/lib/reveal-motion";
import { InputBarConversationContext } from "./input-bar-conversation-context";
import { useStandaloneNativeReveal } from "./use-standalone-native-reveal";
import "./standalone-bar.css";
import "./standalone-bar-liquid-glass.css";

interface Props {
  value: string;
  chip: StandaloneBarChip | null;
  placeholder: StandaloneBarPlaceholder;
  disabled: boolean;
  canSubmit: boolean;
  expanded: boolean;
  multiline: boolean;
  nativeSurface: boolean;
  suspended: boolean;
  revealStage: RevealStage;
  dotInsets: { top: number; right: number; bottom: number; left: number };
  images: NonNullable<InputBarProps["images"]>;
  annotationSummary?: ReactNode;
  inputRef: Ref<ComposerFieldHandle>;
  isStreaming: boolean;
  focusedAgent: InputBarProps["focusedAgent"];
  conversationController?: InputBarProps["conversationController"];
  workerControls: InputBarProps["workerControls"];
  mic?: DictationMicControl;
  providers: InputBarProps["providers"];
  onUpdateAgentModel: InputBarProps["onUpdateAgentModel"];
  onUpdateAgentEffort: InputBarProps["onUpdateAgentEffort"];
  onStop: InputBarProps["onStop"];
  companionDisconnected: InputBarProps["companionDisconnected"];
  onFixCompanion: InputBarProps["onFixCompanion"];
  onRemoveImage: InputBarProps["onRemoveImage"];
  onImageFile?: InputBarProps["onImageFile"];
  onChange: (value: string, chipOffset?: number) => void;
  onChipRemoved: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  onPaste: (event: ClipboardEvent<HTMLElement>) => void;
  onSubmit: (event: FormEvent) => void;
  onEngage: () => void;
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
}

function StandaloneWorkerContext({
  focusedAgent,
  usage,
  onOpenChange,
}: {
  focusedAgent: InputBarProps["focusedAgent"];
  usage: { used: number; max: number };
  onOpenChange: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => () => onOpenChange(false), [onOpenChange]);
  return (
    <InputBarConversationContext
      focusedAgent={focusedAgent ?? null}
      workerUsage={usage}
      density="standalone"
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        onOpenChange(nextOpen);
      }}
    />
  );
}

export function StandaloneBarShell({
  value,
  chip,
  placeholder,
  disabled,
  canSubmit,
  expanded,
  multiline,
  nativeSurface,
  suspended,
  revealStage,
  dotInsets,
  images,
  annotationSummary,
  inputRef,
  isStreaming,
  focusedAgent,
  conversationController,
  workerControls,
  mic,
  providers,
  onUpdateAgentModel,
  onUpdateAgentEffort,
  onStop,
  companionDisconnected,
  onFixCompanion,
  onRemoveImage,
  onImageFile,
  onChange,
  onChipRemoved,
  onKeyDown,
  onPaste,
  onSubmit,
  onEngage,
  popoverOpen,
  onPopoverOpenChange,
}: Props) {
  const t = useT(inputBarMessages);
  const { shellRef, nativeReveal, fallbackDurationMs } =
    useStandaloneNativeReveal({
      stage: revealStage,
      dotInsets,
      enabled: nativeSurface,
    });
  const workerControl = workerControls?.control;
  const usage = workerControls?.contextUsage;
  const hasTarget = !placeholder.route.startsWith("@new");
  const showStop = isStreaming || Boolean(workerControl);
  const showActions = expanded || popoverOpen;

  return (
    <form
      ref={shellRef}
      onSubmit={onSubmit}
      className={cn(
        surfaceRoleClassName("chrome"),
        "denker-input-bar-shell standalone-bar-shell grid overflow-hidden",
        showActions && "standalone-bar-expanded",
      )}
      data-testid="standalone-bar"
      data-reveal-stage={revealStage}
      data-native-reveal={nativeReveal || undefined}
      data-expanded={showActions}
      data-multiline={multiline}
      data-native-glass-suspended={(suspended && !nativeReveal) || undefined}
      style={
        {
          "--standalone-dot-top": `${dotInsets.top}px`,
          "--standalone-dot-right": `${dotInsets.right}px`,
          "--standalone-dot-bottom": `${dotInsets.bottom}px`,
          "--standalone-dot-left": `${dotInsets.left}px`,
          "--standalone-reveal-duration": `${fallbackDurationMs ?? REVEAL_MS}ms`,
        } as CSSProperties
      }
      {...surfaceRoleAttributes(
        "chrome",
        nativeSurface
          ? {
              nativeLevel: "root",
              nativeGroup: "input-bar",
            }
          : {},
      )}
    >
      <div className="min-w-0">
        {images.length > 0 ? (
          <ImagePreviewStrip
            inset
            images={images}
            onRemove={(id) => onRemoveImage?.(id)}
          />
        ) : null}
        {annotationSummary}
        <div className="grid min-h-9 min-w-0 px-3.5 py-1">
          <StandaloneBarField
            ref={inputRef}
            value={value}
            chip={chip}
            placeholder={placeholder}
            disabled={disabled}
            onChange={onChange}
            onChipRemoved={onChipRemoved}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            onEngage={onEngage}
          />
        </div>
      </div>
      <div
        className="standalone-bar-actions-slot"
        aria-hidden={!showActions}
        inert={!showActions}
      >
        <div
          className="flex min-h-7 items-center justify-end gap-0.5 px-3.5 pb-1 text-appkit-caption leading-4"
          data-testid="standalone-bar-actions"
        >
          <ComposerFilePicker onFile={onImageFile} disabled={disabled} />
          {workerControls?.queuedCount ? (
            <span
              className="mr-auto text-appkit-mini text-muted"
              data-testid="input-bar-queued"
            >
              {t("queued", { count: workerControls.queuedCount })}
            </span>
          ) : null}
          {companionDisconnected && onFixCompanion ? (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={onFixCompanion}
              aria-label={t("fixCompanion")}
              data-testid="input-bar-fix-companion"
            >
              <Icons.Wrench className="h-4 w-4" />
            </Button>
          ) : focusedAgent ? (
            <StandaloneAgentControls
              focusedAgent={focusedAgent}
              conversationController={conversationController}
              hasTarget={hasTarget}
              workerUsage={usage}
              providers={providers}
              onUpdateAgentModel={onUpdateAgentModel}
              onUpdateAgentEffort={onUpdateAgentEffort}
              onModelOpenChange={onPopoverOpenChange}
            />
          ) : workerControls?.modelLabel ? (
            <span
              className="max-w-[100px] truncate px-1.5 text-secondary"
              data-testid="standalone-bar-model"
            >
              {workerControls.modelLabel}
            </span>
          ) : null}
          {hasTarget &&
          usage &&
          (!focusedAgent || (companionDisconnected && onFixCompanion)) ? (
            <StandaloneWorkerContext
              focusedAgent={
                workerControls?.contextMenuAgent ?? focusedAgent ?? null
              }
              usage={usage}
              onOpenChange={onPopoverOpenChange}
            />
          ) : null}
          {showStop ? (
            <Button
              type="button"
              variant="ghost"
              size="iconSm"
              onClick={() =>
                workerControl ? workerControl.onClick() : onStop?.()
              }
              disabled={workerControl?.disabled}
              aria-label={
                workerControl?.action === "resume" ? t("resume") : t("stop")
              }
              data-testid={
                workerControl?.action === "resume"
                  ? "input-bar-resume"
                  : "input-bar-stop"
              }
            >
              {workerControl?.action === "resume" ? (
                <Icons.Play className="h-4 w-4" />
              ) : (
                <Icons.Square className="h-4 w-4" />
              )}
            </Button>
          ) : null}
          {mic ? <InputBarMicButton mic={mic} density="standalone" /> : null}
          <Button
            type={canSubmit ? "submit" : "button"}
            variant="ghost"
            size="iconSm"
            disabled={disabled || !canSubmit}
            aria-label={t("send")}
            data-testid="standalone-bar-send"
            className={
              canSubmit
                ? "text-accent hover:text-accent"
                : "text-subtle disabled:opacity-100"
            }
          >
            <Icons.ArrowUp className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </form>
  );
}

/** Shared model and context controls for compact composers. */
export function StandaloneAgentControls({
  focusedAgent,
  conversationController,
  providers,
  onUpdateAgentModel,
  onUpdateAgentEffort,
  onModelOpenChange,
  popoverScope,
  workerUsage,
}: {
  focusedAgent: InputBarProps["focusedAgent"];
  conversationController?: InputBarProps["conversationController"];
  hasTarget: boolean;
  providers: InputBarProps["providers"];
  onUpdateAgentModel: InputBarProps["onUpdateAgentModel"];
  onUpdateAgentEffort: InputBarProps["onUpdateAgentEffort"];
  onModelOpenChange?: (open: boolean) => void;
  popoverScope?: "tray";
  workerUsage?: { used: number; max: number } | null;
}) {
  return (
    <InputBarModelPill
      focusedAgent={focusedAgent ?? null}
      conversationController={conversationController}
      workerUsage={workerUsage}
      providers={providers ?? []}
      onUpdateModel={onUpdateAgentModel ?? (async () => {})}
      onUpdateEffort={onUpdateAgentEffort ?? (async () => {})}
      density="standalone"
      onOpenChange={onModelOpenChange}
      popoverScope={popoverScope}
    />
  );
}
